/**
 * Venue API.
 *
 * Three engine behaviours the UI has to be built around, all verified against the
 * running service rather than carried over from task 26:
 *
 *   1. GET /venue/profile answers 404 — not null — when no profile exists yet.
 *      getMyVenue() translates that to null so the "may not exist yet" state is
 *      an ordinary value, matching the artist portal.
 *
 *   2. DATE columns come back as instants, not calendar dates:
 *      "2026-11-04T00:00:00.000Z". Any keying or comparison must normalize first,
 *      which is what dateKey() is for.
 *
 *   3. A confirmed booking writes a hold onto the calendar, but setAvailability
 *      does NOT clear booked_by_event. A venue can therefore free a date the
 *      engine still considers booked. The calendar renders those dates read-only
 *      because the engine will not refuse the write.
 */

import { api, ApiError } from './apiClient';

/**
 * Postgres DATE -> "YYYY-MM-DD", whatever the server's timezone did to it.
 * Never use `new Date(value)` to key a calendar cell: on the UTC deploy box a
 * DATE serializes to midnight Z, which is the previous day for anyone west of
 * Greenwich.
 */
export const dateKey = (value: string | Date): string =>
  (value instanceof Date ? value.toISOString() : String(value)).slice(0, 10);

/** Today in the viewer's own timezone, as a calendar date. */
export function todayKey(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ── Profile ─────────────────────────────────────────────────────────────────

export interface VenueProfile {
  venueId: string;
  venueName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  capacity: number | null;
  layoutOptions: string | null;
  technicalSpecs: string | null;
  pricingStructure: string | null;
  requirements: string | null;
  images: string[];
  /**
   * Read-only, and currently inert: nothing in the engine sets or reads it, so a
   * venue is bookable regardless. Surfaced honestly rather than dressed up as a
   * working approval state.
   */
  approved: boolean;
  createdAt: string;
}

export interface VenueInput {
  venueName?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  capacity?: number | null;
  layoutOptions?: string | null;
  technicalSpecs?: string | null;
  pricingStructure?: string | null;
  requirements?: string | null;
  images?: string[] | null;
}

/** null when the venue has not created a profile yet — a normal first-run state. */
export async function getMyVenue(): Promise<VenueProfile | null> {
  try {
    return await api.get<VenueProfile>('/venue/profile');
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export const createVenue = (data: VenueInput) =>
  api.post<VenueProfile>('/venue/profile', data);

/** PATCH, not PUT, and it takes the venue id in the path. */
export const updateVenue = (venueId: string, data: VenueInput) =>
  api.patch<VenueProfile>(`/venue/profile/${venueId}`, data);

// ── Availability ────────────────────────────────────────────────────────────

export interface AvailabilityDate {
  date: string;
  available: boolean;
  notes: string | null;
  /** Set by the engine when a booking is confirmed. Non-null means "held". */
  bookedByEvent: string | null;
}

export const getAvailability = (venueId: string, from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const suffix = q.toString() ? `?${q}` : '';
  return api.get<{ venueId: string; dates: AvailabilityDate[] }>(
    `/venue/${venueId}/availability${suffix}`);
};

export const setAvailability = (
  venueId: string,
  entries: { date: string; available: boolean; notes?: string | null }[],
) => api.put<{ venueId: string; dates: AvailabilityDate[] }>(
  `/venue/${venueId}/availability`, { entries });

// ── Bookings ────────────────────────────────────────────────────────────────

/**
 * NULL until an organizer requests, then pending, then confirmed or declined.
 * 'confirmed' is the only terminal state — a declined booking can be re-requested
 * by the organizer, which is why the UI does not describe declining as final.
 */
export type BookingStatus = 'pending' | 'confirmed' | 'declined';

export const BOOKING_LABEL: Record<BookingStatus, string> = {
  pending: 'Awaiting your response',
  confirmed: 'Confirmed',
  declined: 'Declined',
};

export interface VenueBooking {
  eventId: string;
  title: string;
  eventDate: string | null;
  eventTime: string | null;
  doorsTime: string | null;
  eventStatus: string;
  bookingStatus: BookingStatus;
  expectedAttendance: number | null;
  /** The organizer is the venue's counterparty. No buyer appears in this payload. */
  organizer: string;
}

export const listBookings = (status?: BookingStatus) =>
  api.get<{ venueId: string; bookings: VenueBooking[] }>(
    `/venue/bookings${status ? `?status=${status}` : ''}`);

export const respondToBooking = (eventId: string, action: 'confirm' | 'decline') =>
  api.post<{ eventId: string; status: BookingStatus }>(
    `/venue/bookings/${eventId}/respond`, { action });

// ── Event-day stats ─────────────────────────────────────────────────────────

/** Aggregates only. The engine names no buyer column in this query. */
export interface EventDayStats {
  eventId: string;
  title: string;
  eventDate: string | null;
  expectedAttendance: number | null;
  validTickets: number;
  checkedIn: number;
  validScans: number;
  totalScanAttempts: number;
  /** 0..1, already rounded to two places by the engine. */
  checkInRate: number;
  arrivalsByHour: { hour: string; scans: number }[];
}

/** 409 booking_not_confirmed unless the booking is confirmed. */
export const getEventDay = (eventId: string) =>
  api.get<EventDayStats>(`/venue/events/${eventId}/day`);
